import { consoleDebug } from '../../util';

const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;

// Helper function to convert cents to dollars
const centsToDollars = (cents) => roundToTwoDecimals(cents / 100);

function deconstructAddress(addressString) {
    if (!addressString) {
        console.error('Empty address string provided');
        return null;
    }

    // Regular expression to match different parts of the address
    const addressRegex = /^(.*?),\s*(.*?),\s*(\w{2})\s*(\d{5}),\s*(\w+)$/;
    const match = addressString.match(addressRegex);

    if (match) {
        const [, street, city, state, zip, country] = match;

        return {
            street,
            city,
            state,
            zip,
            country: country === 'USA' ? 'US' : country
        };
    } else {
        console.error('Unable to parse address string:', addressString);
        return null;
    }
}

export function didSurfsideLoadPromise(timeout = 10000) {
    return new Promise((resolve, reject) => {
        // Check if Surfside is already loaded and functional
        if (window.surf && typeof window.surf === 'function') {
            consoleDebug('🌊 Surfside SDK already loaded');
            resolve(window.surf);
            return;
        }

        consoleDebug('🌊 Waiting for Surfside SDK to load...');
        
        let attempts = 0;
        const maxAttempts = timeout / 100; // Check every 100ms
        
        const checkSDK = () => {
            attempts++;
            
            // Check if surf exists and is a function
            if (window.surf && typeof window.surf === 'function') {
                consoleDebug(`🌊 Surfside SDK loaded after ${attempts * 100}ms`);
                resolve(window.surf);
                return;
            }

            if (attempts >= maxAttempts) {
                const errorMsg = `Surfside SDK failed to load within ${timeout}ms timeout`;
                consoleDebug(`🌊 ${errorMsg}`);
                reject(new Error(errorMsg));
                return;
            }

            // Continue checking
            setTimeout(checkSDK, 100);
        };

        // Start checking
        checkSDK();
    });
}

// Legacy callback-based version using the improved promise function
export function didSurfsideLoad(callback) {
    didSurfsideLoadPromise()
        .then(() => {
            try {
                callback();
            } catch (error) {
                console.error("Error in Surfside callback:", error);
            }
        })
        .catch((error) => {
            console.error("Surfside SDK failed to load:", error);
        });
}

export async function trackSurfsideEvent(type, product, selectedVariant, quantity = null, specialData = null, retailerId, retailerData, timeout = 5000) {
    try {
        // Wait for Surfside SDK to be loaded
        await didSurfsideLoadPromise(timeout);

        // Add Location Context
        let addressComponents;
        if (retailerData && retailerId && type == 'add') {
            addressComponents = deconstructAddress(retailerData.address);

            if (addressComponents) {
                const currentLocation = {
                    id: retailerId,
                    street: addressComponents.street,
                    city: addressComponents.city,
                    state_label: addressComponents.state,
                    zip: addressComponents.zip,
                    country_code: addressComponents.country,
                };

                window.surf('setLocation', currentLocation);
            }
        }

        // Calculate price correctly based on determination
        // Product prices from API are already in dollars
        let finalPrice;
        if (selectedVariant.specialPriceRec && selectedVariant.specialPriceRec < selectedVariant.priceRec) {
            finalPrice = selectedVariant.specialPriceRec;
        } else {
            finalPrice = selectedVariant.priceRec;
        }

        // Calculate discount
        const regularPrice = selectedVariant.priceRec;
        const discount = selectedVariant.specialPriceRec ? roundToTwoDecimals(regularPrice - finalPrice) : 0;

        const surfsideData = {
            id: product.id,
            name: product.name,
            brand: product.brand?.name,
            category: product.category,
            category2: product.subcategory,
            variant: selectedVariant.option,
            price: finalPrice,
            quantity: quantity || 1,
            discount: discount,
            currency: 'USD'
        };

        if (surfsideData) {
            consoleDebug('🌊 Tracking Surfside Event:', {
                type: type,
                data: surfsideData,
            });
        }

        window.surf('addProduct:range', surfsideData);

        switch (type) {
            case 'add':
                window.surf('setCommerceAction:range', 'add');
                break;
            case 'remove':
                window.surf('setCommerceAction:range', 'remove');
                break;
            case 'detail':
                window.surf('setCommerceAction:range', 'detail');
                break;
            case 'checkout':
                // Add delay for checkout to ensure tracking completes before redirect
                await new Promise(resolve => setTimeout(resolve, 1000));
                window.surf('setCommerceAction:range', 'checkout');
                break;
            default:
                // No action needed for purchase event
                break;
        }

        return true;
    } catch (error) {
        console.error('Error tracking Surfside event (SDK may not have loaded):', error);
        return false;
    }
}

export async function trackSurfsideProductsLoop(checkoutData, type, specialsData = {}, retailerId = null, retailerData = null, timeout = 5000) {
    try {
        // Wait for Surfside SDK to be loaded
        await didSurfsideLoadPromise(timeout);

        let products;

        if (!checkoutData.items || !Array.isArray(checkoutData.items) || checkoutData.items.length === 0) {
            console.error('No products to send to Surfside checkout');
            return false;
        }

        if (checkoutData.priceSummary) {
            // Convert all price values from cents to dollars
            const formattedPriceSummary = {
                discounts: centsToDollars(checkoutData.priceSummary.discounts),
                fees: centsToDollars(checkoutData.priceSummary.fees),
                mixAndMatch: centsToDollars(checkoutData.priceSummary.mixAndMatch),
                rewards: centsToDollars(checkoutData.priceSummary.rewards),
                subtotal: centsToDollars(checkoutData.priceSummary.subtotal),
                taxes: centsToDollars(checkoutData.priceSummary.taxes),
                total: centsToDollars(checkoutData.priceSummary.total)
            };

            // Replace the original price summary with formatted values
            checkoutData.priceSummary = formattedPriceSummary;
        }

        products = checkoutData.items;

        if (products) {
            consoleDebug('🌊 Surfside Products Loop Data:', products);
        }

        // Add location data if available
        if (retailerId && retailerData && retailerData.address) {
            const addressComponents = deconstructAddress(retailerData.address);

            if (addressComponents) {
                const currentLocation = {
                    id: retailerId,
                    street: addressComponents.street,
                    city: addressComponents.city,
                    state_label: addressComponents.state,
                    zip: addressComponents.zip,
                    country_code: addressComponents.country,
                };

                window.surf('setLocation', currentLocation);
            }
        }

        // Track each product
        let successCount = 0;
        for (const product of products) {
            const productData = product.product;
            if (!productData || !productData.variants) {
                console.error('Invalid product data format:', product);
                continue;
            }

            const selectedVariant = productData.variants.find(variant => variant.option === product.option);
            if (!selectedVariant) {
                console.error('Selected variant not found for product:', productData.id);
                continue;
            }

            const specialData = specialsData?.[productData.id];

            const success = await trackSurfsideEvent(type, productData, selectedVariant, product.quantity, specialData, retailerId, retailerData, timeout);
            if (success) successCount++;
        }

        // Only set commerce action if products were successfully added
        if (successCount > 0) {
            window.surf('setCommerceAction:range', type === 'checkout' ? 'checkout' : type);
            return true;
        } else {
            console.error(`Failed to track any products for Surfside ${type} event`);
            return false;
        }
    } catch (error) {
        console.error(`Error in trackSurfsideProductsLoop for ${type} (SDK may not have loaded):`, error);
        return false;
    }
}

export async function trackSurfsidePurchase(orderNumber, order, retailerId, retailerData, timeout = 5000) {
    try {
        // Wait for Surfside SDK to be loaded
        await didSurfsideLoadPromise(timeout);

        // Order totals are already in dollars, not cents
        consoleDebug('Tracking Surfside purchase:', {
            orderNumber,
            orderTotal: order.total,
            itemCount: order.items?.length
        });

        // Add Location Context
        if (retailerData && retailerId) {
            const addressComponents = deconstructAddress(retailerData.address);

            if (addressComponents) {
                const currentLocation = {
                    id: retailerId,
                    street: addressComponents.street,
                    city: addressComponents.city,
                    state_label: addressComponents.state,
                    zip: addressComponents.zip,
                    country_code: addressComponents.country,
                };

                window.surf('setLocation', currentLocation);
            }
        }

        if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
            console.error('No products in order for Surfside purchase tracking');
            return false;
        }

        let successCount = 0;
        
        // FIRST: Add all products
        for (const item of order.items) {
            try {
                const productData = item.product;
                if (!productData || !productData.variants) {
                    console.error('Invalid product data format in order:', item);
                    continue;
                }

                const selectedVariant = productData.variants.find(variant => variant.option === item.option);
                if (!selectedVariant) {
                    console.error('Selected variant not found for product in order:', productData.id);
                    continue;
                }

                // Use the price from the order item (already in dollars)
                // The order object shows price as 1134 (which represents $11.34 in cents)
                const finalPrice = centsToDollars(item.price);

                // Calculate discount if special price exists
                const regularPrice = selectedVariant.priceRec;
                const discount = selectedVariant.specialPriceRec && selectedVariant.specialPriceRec < selectedVariant.priceRec
                    ? roundToTwoDecimals(regularPrice - selectedVariant.specialPriceRec)
                    : 0;

                const surfsideData = {
                    id: productData.id,
                    name: productData.name,
                    brand: productData.brand?.name,
                    category: productData.category,
                    category2: productData.subcategory,
                    variant: selectedVariant.option,
                    price: finalPrice,
                    quantity: item.quantity || 1,
                    discount: discount,
                    currency: 'USD'
                };

                consoleDebug(`Adding product to Surfside purchase: ${productData.name}`, {
                    price: finalPrice,
                    regularPrice: regularPrice,
                    discount: discount
                });

                window.surf('addProduct:range', surfsideData);
                successCount++;
            } catch (error) {
                console.error('Error adding product to Surfside purchase:', error);
            }
        }

        // SECOND: Add the transaction (after all products)
        // Order totals are already in dollars
        const checkoutData = {
            id: orderNumber,
            revenue: order.total,
            tax: order.tax || 0,
            shipping: 0,
            currency: 'USD'
        };

        consoleDebug('Adding Surfside transaction data:', checkoutData);
        window.surf('addTransaction:range', checkoutData);

        // THIRD: Set the commerce action
        // Add a small delay to ensure all data is processed
        await new Promise(resolve => setTimeout(resolve, 100));
        
        window.surf('setCommerceAction:range', 'purchase');
        consoleDebug(`Successfully tracked ${successCount}/${order.items.length} products for Surfside purchase`);

        return successCount > 0;
    } catch (error) {
        console.error('Error tracking Surfside purchase (SDK may not have loaded):', error);
        return false;
    }
}

export function trackSurfsidePurchasePromise(orderNumber, order, retailerId, retailerData, timeout = 10000) {
    return new Promise(async (resolve) => {
        try {
            // WAIT for Surfside SDK to be loaded before proceeding
            await didSurfsideLoadPromise(timeout);
            
            consoleDebug('🌊 Surfside SDK confirmed loaded, proceeding with purchase tracking');

            // Order totals are already in dollars
            consoleDebug('Tracking Surfside purchase:', {
                orderNumber,
                orderSubtotal: order.subtotal,
                orderTax: order.tax,
                orderTotal: order.total,
                itemCount: order.items?.length,
            });

            // Add Location Context
            if (retailerData && retailerId) {
                const addressComponents = deconstructAddress(retailerData.address);

                if (addressComponents) {
                    const currentLocation = {
                        id: retailerId,
                        street: addressComponents.street,
                        city: addressComponents.city,
                        state_label: addressComponents.state,
                        zip: addressComponents.zip,
                        country_code: addressComponents.country,
                    };

                    window.surf('setLocation', currentLocation);
                }
            }

            if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
                console.error('No products in order for Surfside purchase tracking');
                resolve(false);
                return;
            }

            // Debug: Check if window.surf is actually executing
            consoleDebug('🌊 About to add products. window.surf exists:', !!window.surf);
            
            // Clear any previous commerce data
            try {
                window.surf('clearCommerceData:range');
                consoleDebug('🌊 Cleared previous commerce data');
            } catch (error) {
                // This might not be a valid method, ignore if it fails
                consoleDebug('🌊 clearCommerceData not available or failed');
            }

            // STEP 1: Add all products first (as per Surfside docs)
            let successCount = 0;
            for (let i = 0; i < order.items.length; i++) {
                const item = order.items[i];
                try {
                    const productData = item.product;
                    if (!productData || !productData.variants) {
                        console.error('Invalid product data format in order:', item);
                        continue;
                    }

                    const selectedVariant = productData.variants.find(variant => variant.option === item.option);
                    if (!selectedVariant) {
                        console.error('Selected variant not found for product in order:', productData.id);
                        continue;
                    }

                    // Use the price from the order item (convert from cents to dollars)
                    // The order object shows price as 1134 (cents) for $11.34
                    const finalPrice = centsToDollars(item.price);

                    // For discount calculation, use the variant prices (already in dollars)
                    const regularPrice = selectedVariant.priceRec;
                    const discount = selectedVariant.specialPriceRec &&
                        selectedVariant.specialPriceRec < selectedVariant.priceRec ?
                        roundToTwoDecimals(regularPrice - selectedVariant.specialPriceRec) : 0;

                    const surfsideData = {
                        id: productData.id,
                        name: productData.name,
                        list: 'Shopping Cart', // Add list as per Surfside docs
                        brand: productData.brand?.name,
                        category: productData.category,
                        category2: productData.subcategory,
                        variant: selectedVariant.option,
                        price: finalPrice,
                        quantity: item.quantity || 1,
                        position: i + 1, // Add position as per Surfside docs
                        discount: discount,
                        currency: 'USD'
                    };

                    consoleDebug(`🌊 Adding product to purchase: ${productData.name}`, surfsideData);

                    window.surf('addProduct:range', surfsideData);
                    successCount++;
                    
                    // Add small delay between products
                    if (i < order.items.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 50));
                    }
                } catch (error) {
                    console.error('Error adding product to Surfside purchase:', error);
                }
            }

            if (successCount === 0) {
                console.error('No products were successfully added for purchase tracking');
                resolve(false);
                return;
            }

            // STEP 2: Add the transaction data (after products, as per Surfside docs)
            // Order totals are already in dollars
            const transactionData = {
                id: orderNumber,
                affiliation: retailerData?.name || 'Online Store', // Add affiliation
                revenue: order.subtotal, // Already in dollars
                tax: order.tax || 0, // Already in dollars
                shipping: order.shipping || 0, // Already in dollars
                list: 'Shopping Cart', // Add list to match products
                currency: 'USD'
            };

            // Add coupon if available
            if (order.coupon) {
                transactionData.coupon = order.coupon;
            }

            consoleDebug('🌊 Adding transaction data:', transactionData);
            
            // Try wrapping in try-catch to see if there's an error
            try {
                window.surf('addTransaction:range', transactionData);
                consoleDebug('🌊 Transaction added successfully');
            } catch (error) {
                console.error('🌊 Error adding transaction:', error);
            }

            // STEP 3: Set the commerce action to 'purchase' - NO DELAY
            // Try setting the action immediately after transaction
            try {
                window.surf('setCommerceAction:range', 'purchase');
                consoleDebug('🌊 Purchase action set successfully');
            } catch (error) {
                console.error('🌊 Error setting purchase action:', error);
            }
            consoleDebug('🌊 Purchase action set - tracking complete');
            consoleDebug(`🌊 Successfully tracked ${successCount}/${order.items.length} products for Surfside purchase`);
            
            resolve(true);

        } catch (error) {
            // This catch will handle didSurfsideLoadPromise rejections
            console.error('Error tracking Surfside purchase (SDK may not have loaded):', error);
            // Sentry.captureException(error);
            resolve(false);
        }
    });
}

export function trackSurfsideProductsLoopPromise(checkoutData, type, specialsData = {}, retailerId = null, retailerData = null, timeout = 5000) {
    return new Promise((resolve) => {
        // Use the improved didSurfsideLoadPromise
        didSurfsideLoadPromise(timeout)
            .then((surf) => {
                try {
                    if (!checkoutData.items || !Array.isArray(checkoutData.items) || checkoutData.items.length === 0) {
                        console.error('No products to send to Surfside checkout');
                        resolve(false);
                        return;
                    }

                    // Format price data - convert from cents to dollars
                    if (checkoutData.priceSummary) {
                        const formattedPriceSummary = {
                            discounts: centsToDollars(checkoutData.priceSummary.discounts),
                            fees: centsToDollars(checkoutData.priceSummary.fees),
                            mixAndMatch: centsToDollars(checkoutData.priceSummary.mixAndMatch),
                            rewards: centsToDollars(checkoutData.priceSummary.rewards),
                            subtotal: centsToDollars(checkoutData.priceSummary.subtotal),
                            taxes: centsToDollars(checkoutData.priceSummary.taxes),
                            total: centsToDollars(checkoutData.priceSummary.total)
                        };

                        checkoutData.priceSummary = formattedPriceSummary;
                    }

                    const products = checkoutData.items;
                    consoleDebug('🌊 Surfside Products Loop Data:', products.length, 'products');

                    // Add location data if available
                    if (retailerId && retailerData && retailerData.address) {
                        const addressComponents = deconstructAddress(retailerData.address);

                        if (addressComponents) {
                            const currentLocation = {
                                id: retailerId,
                                street: addressComponents.street,
                                city: addressComponents.city,
                                state_label: addressComponents.state,
                                zip: addressComponents.zip,
                                country_code: addressComponents.country,
                            };

                            window.surf('setLocation', currentLocation);
                        }
                    }

                    let successCount = 0;
                    products.forEach(product => {
                        const productData = product.product;
                        if (!productData || !productData.variants) {
                            console.error('Invalid product data format:', product);
                            return;
                        }

                        const selectedVariant = productData.variants.find(variant => variant.option === product.option);
                        if (!selectedVariant) {
                            console.error('Selected variant not found for product:', productData.id);
                            return;
                        }

                        // Calculate price correctly - product prices from API are in dollars
                        let finalPrice;
                        if (selectedVariant.specialPriceRec && selectedVariant.specialPriceRec < selectedVariant.priceRec) {
                            finalPrice = selectedVariant.specialPriceRec;
                        } else {
                            finalPrice = selectedVariant.priceRec;
                        }

                        // Calculate discount
                        const regularPrice = selectedVariant.priceRec;
                        const discount = selectedVariant.specialPriceRec ? roundToTwoDecimals(regularPrice - finalPrice) : 0;

                        // Create the product data
                        const surfsideData = {
                            id: productData.id,
                            name: productData.name,
                            brand: productData.brand?.name,
                            category: productData.category,
                            category2: productData.subcategory,
                            variant: selectedVariant.option,
                            price: finalPrice,
                            quantity: product.quantity || 1,
                            discount: discount,
                            currency: 'USD'
                        };

                        window.surf('addProduct:range', surfsideData);
                        successCount++;
                    });

                    if (successCount > 0) {
                        // For checkout, add transaction data if available
                        if (type === 'checkout' && checkoutData.priceSummary) {
                            setTimeout(() => {
                                const transactionData = {
                                    id: checkoutData.id || `checkout-${Date.now()}`,
                                    revenue: checkoutData.priceSummary.subtotal, // Already converted to dollars above
                                    tax: checkoutData.priceSummary.taxes || 0, // Already converted to dollars above
                                    shipping: 0,
                                    list: 'Shopping Cart',
                                    step: 1, // checkout step
                                    currency: 'USD'
                                };

                                window.surf('addTransaction:range', transactionData);
                                consoleDebug('🌊 Surfside Transaction Data set for checkout');

                                setTimeout(() => {
                                    window.surf('setCommerceAction:range', 'checkout');
                                    consoleDebug(`🌊 Surfside checkout event tracking complete`);
                                    resolve(true);
                                }, 50);
                            }, 50);
                        } else {
                            window.surf('setCommerceAction:range', type);
                            consoleDebug(`🌊 Successfully added ${successCount} products to Surfside ${type} event`);
                            resolve(true);
                        }
                    } else {
                        console.error(`🌊 Failed to track any products for Surfside ${type} event`);
                        resolve(false);
                    }
                } catch (error) {
                    console.error(`Error in trackSurfsideProductsLoopPromise for ${type}:`, error);
                    resolve(false);
                }
            })
            .catch((error) => {
                console.error('Surfside SDK loading failed:', error);
                resolve(false);
            });
    });
}
